import React, { useEffect, useRef } from 'react';
import { Modal } from 'antd';
import { useWindowSize } from 'usehooks-ts';
import { PubsubChannelType } from 'sb-lib/types';
import { useAppDispatch, useAppSelector } from 'shared/store';
import {
  fetchAppNotifications,
  readAppNotification,
  selectAppNotificationsUnreadCount,
  uiWantsModalOpenChanged,
} from 'shared/store/app-notification-slice';
import { selectActiveUser } from 'shared/store/shared-selectors';
import { useUserSocketSubscription } from 'shared/hooks';
import { Icon } from 'shared/components/Icon';
import { Button } from 'shared/components/Button';
import { AppNotificationList } from '../AppNotificationList';
import { AppNotificationsSkeletal } from '../AppNotificationsSkeletal';
import { AppNotificationSoundPlayer } from './AppNotificationsSoundPlayer';
import './style.less';

const NAME = 'AppNotificationsModal';

export const AppNotificationsModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const ws = useWindowSize();
  const skeletalContainerRef = useRef<HTMLDivElement>(null);
  const authedUser = useAppSelector(selectActiveUser);
  const unreadCount = useAppSelector(selectAppNotificationsUnreadCount);
  const { hasLoaded, isLoading, getListError, uiWantsModalOpen } = useAppSelector(
    s => s.appNotification,
  );

  // Subscribe to user app-notifications channel
  useUserSocketSubscription(PubsubChannelType['app-notifications.user'], true);

  // Initial load
  useEffect(() => {
    if (authedUser && !hasLoaded && !isLoading && !getListError) {
      dispatch(fetchAppNotifications());
    }
  }, [dispatch, getListError, authedUser, hasLoaded, isLoading]);

  const handleClose = () => {
    dispatch(uiWantsModalOpenChanged(false));
  };

  const handleReadAll = async () => {
    await dispatch(readAppNotification());
  };

  // See: https://github.com/ant-design/ant-design/tree/237ab9a3242f5ff73341196dbf3838b27a0dc501/components/style/core/motion
  // for possible transitionNames and how to define new ones. Took a bit of digging to find this as its undocumented afaik.
  return (
    <>
      <Modal
        visible={uiWantsModalOpen}
        transitionName="move-left"
        onOk={handleClose}
        onCancel={handleClose}
        className={NAME}
        width={400}
        style={{ top: '10px', paddingBottom: '10px', height: `${ws.height - 10}px` }}
        title={
          <>
            <div>Notifications</div>
            <div>
              <Button type="ghost" disabled={unreadCount === 0} onClick={handleReadAll}>
                <Icon icon="checkmark" />
                Mark as read
              </Button>
              <Icon onClick={handleClose} icon="close" />
            </div>
          </>
        }
        footer={null}
        closable={false}
      >
        <AppNotificationList />
      </Modal>
      <div ref={skeletalContainerRef} className="AppNotificationsSkeletalContainer"></div>
      <AppNotificationsSkeletal containerRef={skeletalContainerRef} />
      <AppNotificationSoundPlayer />
    </>
  );
};
